import { inject } from '@angular/core';
import { type CanActivateFn, Router } from '@angular/router';

import { map, take } from 'rxjs';

import { ProfileService } from '@core/services/profile.service';

import { type TenantFeature } from '@models/tenant-config.model';

/**
 * Factory guard — allows navigation only when the given feature is enabled for the current
 * tenant, redirecting to /access-denied otherwise. An unreachable server is not "feature off":
 * that lands on /access-denied?reason=unreachable with a retry back to the wanted page.
 *
 * Usage in route config:
 *   canActivate: [authGuard, chefGuard, featureGuard('Absences')]
 */
export const featureGuard = (feature: TenantFeature): CanActivateFn => {
  return (_route, state) => {
    const profile = inject(ProfileService);
    const router = inject(Router);

    return profile.featureAccess(feature).pipe(
      take(1),
      map((access) => {
        if (access === 'enabled') {return true;}
        return access === 'unreachable'
          ? router.createUrlTree(['/access-denied'], { queryParams: { reason: 'unreachable', returnUrl: state.url } })
          : router.createUrlTree(['/access-denied'], { queryParams: { reason: 'feature-disabled', feature } });
      }),
    );
  };
};
